import { createFileRoute } from '@tanstack/react-router';
import { BarChart3, TrendingUp, AlertTriangle, Clock, Users, DollarSign, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/analytics')({
  component: AnalyticsPage,
});

const features = [
  {
    iconName: "BarChart3",
    title: "Real-Time Dashboards",
    description: "Monitor API usage, performance, and errors as they happen with live-updating charts.",
  },
  {
    iconName: "TrendingUp",
    title: "Usage Trends",
    description: "Analyze usage patterns over time. Identify growth trends and optimization opportunities.",
  },
  {
    iconName: "AlertTriangle",
    title: "Error Tracking",
    description: "Quickly identify and diagnose issues with detailed error breakdowns and stack traces.",
  },
  {
    iconName: "Clock",
    title: "Latency Monitoring",
    description: "Track response times across providers. Set SLO targets and get alerts when exceeded.",
  },
  {
    iconName: "Users",
    title: "User Analytics",
    description: "Understand how different users and teams use the platform. Identify power users.",
  },
  {
    iconName: "DollarSign",
    title: "Cost Analytics",
    description: "Track spending across providers, teams, and projects. Set budgets and alerts.",
  },
];

const metrics = [
  { category: "API Metrics", items: ["Request volume", "Response times", "Error rates", "Status codes", "Throughput"] },
  { category: "AI/LLM Metrics", items: ["Token usage", "Model distribution", "Cost per request", "Latency by model", "Cache hit rate"] },
  { category: "Agent Metrics", items: ["Execution count", "Success rate", "Tool usage", "Session duration", "User satisfaction"] },
  { category: "Business Metrics", items: ["Active users", "Integration usage", "Feature adoption", "Growth rate", "Churn indicators"] },
];

const faqItems = [
  {
    question: "What's the data retention period?",
    answer: "Standard plans include 90 days of detailed data. Aggregated metrics are retained for 1 year. Enterprise plans can extend retention as needed.",
  },
  {
    question: "Can I export analytics data?",
    answer: "Yes, data can be exported as CSV or JSON. You can also stream events to your own data warehouse using webhooks or our export API.",
  },
  {
    question: "Can I create custom dashboards?",
    answer: "Yes, create custom dashboards with the metrics that matter to your team. Save, share, and schedule dashboard reports.",
  },
  {
    question: "How are alerts configured?",
    answer: "Set up alerts based on thresholds, anomalies, or conditions. Receive notifications via email, Slack, or webhooks when issues occur.",
  },
];

function AnalyticsPage() {
  return (
    <div>
      <ContentHeader
        title="Real-time Analytics"
        description="Comprehensive visibility into your APIs, AI agents, and integrations. Monitor performance, track costs, and gain insights with powerful analytics."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "Analytics" },
        ]}
        badge="Feature"
      />

      {/* Features */}
      <ContentSection
        id="features"
        title="Analytics Features"
        background="muted"
      >
        <FeatureShowcase features={features} columns={3} variant="cards" />
      </ContentSection>

      {/* Metrics */}
      <ContentSection
        id="metrics"
        title="Metrics We Track"
        description="Comprehensive metrics across all dimensions of your platform usage."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {metrics.map((metric) => (
            <div key={metric.category} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{metric.category}</h3>
              <ul className="mt-4 space-y-2">
                {metric.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Dashboard Preview */}
      <ContentSection
        id="dashboard"
        title="Dashboard Overview"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h4 className="text-sm text-muted-foreground">Total Requests</h4>
            <div className="mt-2 text-3xl font-bold">2.4M</div>
            <div className="mt-1 text-sm text-green-500">+12% from last week</div>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h4 className="text-sm text-muted-foreground">Avg Latency</h4>
            <div className="mt-2 text-3xl font-bold">45ms</div>
            <div className="mt-1 text-sm text-green-500">-8% from last week</div>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h4 className="text-sm text-muted-foreground">Error Rate</h4>
            <div className="mt-2 text-3xl font-bold">0.12%</div>
            <div className="mt-1 text-sm text-muted-foreground">Within SLO target</div>
          </div>
        </div>
      </ContentSection>

      {/* Alerting */}
      <ContentSection id="alerting" title="Alerting & Notifications">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-semibold text-lg mb-4">Never Miss an Issue</h3>
            <p className="text-muted-foreground mb-6">
              Set up intelligent alerts that notify you when something needs attention.
              Reduce noise with smart grouping and escalation policies.
            </p>
            <ul className="space-y-3">
              {[
                "Threshold-based alerts (e.g., error rate > 1%)",
                "Anomaly detection for unusual patterns",
                "SLO breach notifications",
                "Budget and spending alerts",
                "Custom metric conditions",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold mb-4">Notification Channels</h3>
            <div className="space-y-4">
              {["Email", "Slack", "Microsoft Teams", "PagerDuty", "Webhook", "SMS"].map((channel) => (
                <div key={channel} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>{channel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Get Visibility Into Your Platform"
        description="Start tracking your APIs, agents, and integrations with comprehensive analytics."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "View Pricing", href: "/pricing" }}
        background="primary"
      />
    </div>
  );
}
